import { allowsRatedPlay, defaultTimeControlForClass } from '../chess/game.js';
import { VARIANTS } from '../chess/variants.js';
import { parseGameId, isPlayerIdBoundToPeer, isValidPlayerId, playerIdFromPeerId } from '../security/input.js';
import { publicUrl } from '../app/publicUrl.js';
import { hasValidAntiSpam, withAntiSpam } from './antiSpam.js';
import { lobbyDebug, lobbyDebugReport } from './lobbyDebug.js';

export const CHALLENGE_MESSAGE_TYPE = 'dechess-challenge';
export const CHALLENGE_DECLINE_TYPE = 'dechess-challenge-decline';
export const CHALLENGE_ACK_TYPE = 'dechess-challenge-ack';
export const CHALLENGE_TTL_MS = 15 * 60 * 1000;

export const CHALLENGE_JOIN_WAIT_OPTIONS = [
  { id: '15m', label: '15 minutes', ms: 15 * 60 * 1000 },
  { id: '30m', label: '30 minutes', ms: 30 * 60 * 1000 },
  { id: '60m', label: '1 hour', ms: 60 * 60 * 1000 },
  { id: '180m', label: '3 hours', ms: 3 * 60 * 60 * 1000 }
];

export const DEFAULT_CHALLENGE_JOIN_WAIT_MS = CHALLENGE_JOIN_WAIT_OPTIONS[0].ms;

export const CHALLENGE_START_MODES = {
  IMMEDIATE: 'immediate',
  DELAY: 'delay',
  SCHEDULED: 'scheduled'
};

export const CHALLENGE_DELAY_OPTIONS = [
  { id: '5m', label: '5 minutes', ms: 5 * 60 * 1000 },
  { id: '10m', label: '10 minutes', ms: 10 * 60 * 1000 },
  { id: '15m', label: '15 minutes', ms: 15 * 60 * 1000 },
  { id: '30m', label: '30 minutes', ms: 30 * 60 * 1000 },
  { id: '60m', label: '1 hour', ms: 60 * 60 * 1000 }
];

export const MATCH_FORFEIT_GRACE_MS = 5 * 60 * 1000;

export function resolveStartProposal({
  mode = CHALLENGE_START_MODES.IMMEDIATE,
  delayMs = CHALLENGE_DELAY_OPTIONS[0].ms,
  scheduledInput = '',
  sentAt = Date.now()
}) {
  if (mode === CHALLENGE_START_MODES.DELAY) {
    const delay = CHALLENGE_DELAY_OPTIONS.find((entry) => entry.ms === delayMs)?.ms
      || CHALLENGE_DELAY_OPTIONS[0].ms;
    return {
      startMode: CHALLENGE_START_MODES.DELAY,
      scheduledStartAt: sentAt + delay,
      forfeitGraceMs: MATCH_FORFEIT_GRACE_MS
    };
  }
  if (mode === CHALLENGE_START_MODES.SCHEDULED) {
    const scheduledStartAt = parseScheduledStartInput(scheduledInput, sentAt);
    if (!scheduledStartAt) {
      return {
        startMode: CHALLENGE_START_MODES.IMMEDIATE,
        scheduledStartAt: null,
        forfeitGraceMs: null
      };
    }
    return {
      startMode: CHALLENGE_START_MODES.SCHEDULED,
      scheduledStartAt,
      forfeitGraceMs: MATCH_FORFEIT_GRACE_MS
    };
  }
  return {
    startMode: CHALLENGE_START_MODES.IMMEDIATE,
    scheduledStartAt: null,
    forfeitGraceMs: null
  };
}

export function resolveJoinWaitMs(value) {
  const option = CHALLENGE_JOIN_WAIT_OPTIONS.find((entry) => entry.ms === value);
  return option?.ms || DEFAULT_CHALLENGE_JOIN_WAIT_MS;
}

export function parseScheduledStartInput(value, now = Date.now()) {
  if (!value) {
    return null;
  }
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp) || timestamp <= now) {
    return null;
  }
  return timestamp;
}

export function toDateTimeLocalValue(timestamp) {
  if (!timestamp) {
    return '';
  }
  const date = new Date(timestamp);
  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function formatChallengeDeadline(timestamp) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(timestamp);
}

export function formatCountdown(remainingMs) {
  if (remainingMs <= 0) {
    return '0:00';
  }
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function boardConfigFromLeaderboard({ mode, timeClass, variant }) {
  const timeControl = defaultTimeControlForClass(timeClass);
  return {
    timeClass,
    timeControl,
    variant: mode === 'standard' ? 'standard' : variant,
    playMode: mode === 'standard' ? 'standard' : 'variants',
    rated: allowsRatedPlay(timeControl)
  };
}

export function buildChallengePayload({
  challengeId = crypto.randomUUID(),
  gameId,
  challenger,
  targetPlayerId,
  timeControl,
  variant = 'standard',
  rated = false,
  sentAt = Date.now(),
  joinWaitMs = DEFAULT_CHALLENGE_JOIN_WAIT_MS,
  scheduledStartAt = null,
  startMode = CHALLENGE_START_MODES.IMMEDIATE,
  forfeitGraceMs = null
}) {
  const waitMs = resolveJoinWaitMs(joinWaitMs);
  const baseExpiry = sentAt + waitMs;
  const expiresAt = scheduledStartAt && scheduledStartAt > baseExpiry
    ? scheduledStartAt + (forfeitGraceMs ?? MATCH_FORFEIT_GRACE_MS)
    : baseExpiry;
  return {
    challengeId,
    gameId,
    challenger,
    targetPlayerId,
    timeControl,
    variant,
    rated: Boolean(rated) && allowsRatedPlay(timeControl),
    sentAt,
    joinWaitMs: expiresAt - sentAt,
    startMode,
    scheduledStartAt: scheduledStartAt || null,
    forfeitGraceMs: scheduledStartAt ? (forfeitGraceMs ?? MATCH_FORFEIT_GRACE_MS) : null,
    expiresAt
  };
}

export function validateChallengePayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  if (!parseGameId(payload.gameId)) {
    return false;
  }
  if (!isValidPlayerId(payload.targetPlayerId)) {
    return false;
  }
  if (!payload.challenger?.playerId || !isValidPlayerId(payload.challenger.playerId)) {
    return false;
  }
  if (!payload.challenger?.peerId) {
    return false;
  }
  if (!payload.timeControl || typeof payload.timeControl !== 'string') {
    return false;
  }
  if (typeof payload.rated !== 'boolean') {
    return false;
  }
  if (typeof payload.sentAt !== 'number' || typeof payload.expiresAt !== 'number') {
    return false;
  }
  if (
    payload.scheduledStartAt != null
    && (typeof payload.scheduledStartAt !== 'number' || payload.scheduledStartAt <= payload.sentAt)
  ) {
    return false;
  }
  if (payload.joinWaitMs != null && typeof payload.joinWaitMs !== 'number') {
    return false;
  }
  if (payload.startMode != null && typeof payload.startMode !== 'string') {
    return false;
  }
  if (payload.forfeitGraceMs != null && typeof payload.forfeitGraceMs !== 'number') {
    return false;
  }
  return true;
}

export function isChallengeExpired(challenge, now = Date.now()) {
  return !challenge?.expiresAt || challenge.expiresAt <= now;
}

export function peerAcceptsChallenges(peer) {
  if (!peer?.metadata) {
    return true;
  }
  if (peer.metadata.openToChallenges === undefined) {
    return true;
  }
  return Boolean(peer.metadata.openToChallenges);
}

export function findPeerForPlayer(remotePeers, playerId) {
  const normalized = String(playerId || '').toLowerCase();
  return remotePeers.find((peer) => {
    const claimed = peer.metadata?.playerId?.toLowerCase();
    const derived = playerIdFromPeerId(peer.peerId);
    return claimed === normalized || derived === normalized;
  }) || null;
}

export function challengeJoinUrl(challenge) {
  const game = encodeURIComponent(challenge.gameId);
  const peer = encodeURIComponent(challenge.challenger.peerId);
  const path = `${publicUrl()}?game=${game}&role=join&peer=${peer}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    try {
      return new URL(path, window.location.origin).href;
    } catch {
      return path;
    }
  }
  return path;
}

export function formatChallengeSchedule(challenge) {
  return formatStartCommitment(challenge);
}

export function formatStartCommitment(challenge) {
  if (!challenge?.scheduledStartAt) {
    return 'Start when both players are online and confirm at the board.';
  }
  const graceMin = Math.round((challenge.forfeitGraceMs ?? MATCH_FORFEIT_GRACE_MS) / 60000);
  const modeLabel = challenge.startMode === CHALLENGE_START_MODES.DELAY
    ? 'Start in'
    : 'Scheduled start';
  return `${modeLabel} ${formatChallengeDeadline(challenge.scheduledStartAt)} · ${graceMin}-minute forfeit grace if a player fails to appear and confirm.`;
}

export function formatChallengeLabel({ timeControl, variant = 'standard', rated = false }) {
  const variantLabel = variant === 'standard'
    ? ''
    : `${VARIANTS[variant]?.label || variant} `;
  const ratedLabel = rated ? 'Rated' : 'Unrated';
  return `${ratedLabel} · ${variantLabel}${timeControl}`.replace(/\s+/g, ' ').trim();
}

export async function sealChallengePayload(challenge) {
  if (!challenge || typeof challenge !== 'object') {
    return null;
  }
  if (challenge.antiSpam) {
    return challenge;
  }
  return withAntiSpam(challenge, 'challenge');
}

export async function parseIncomingChallenge(message, {
  playerId,
  openToChallenges,
  blockedPlayerIds = [],
  now = Date.now()
}) {
  if (message.type !== CHALLENGE_MESSAGE_TYPE || !openToChallenges) {
    return null;
  }
  const payload = message.payload;
  if (!validateChallengePayload(payload)) {
    return null;
  }
  if (payload.targetPlayerId.toLowerCase() !== String(playerId).toLowerCase()) {
    return null;
  }
  if (!isPlayerIdBoundToPeer(payload.challenger.playerId, message.senderId)) {
    return null;
  }
  if (payload.challenger.peerId !== message.senderId) {
    return null;
  }
  const blocked = new Set(
    (blockedPlayerIds || []).map((id) => String(id || '').toLowerCase()).filter(Boolean)
  );
  if (blocked.has(String(payload.challenger.playerId || '').toLowerCase())) {
    return null;
  }
  if (isChallengeExpired(payload, now)) {
    return null;
  }
  // WebRTC challenges from modern clients carry antiSpam; PeerBasket/hello may omit it.
  if (payload.antiSpam && !(await hasValidAntiSpam(payload, 'challenge'))) {
    return null;
  }
  return {
    ...payload,
    receivedAt: now,
    senderId: message.senderId
  };
}

/** Stable keys so PeerBasket (`basket-${gameId}`) and WebRTC UUIDs collapse. */
export function challengeIdentityKeys(challenge) {
  const keys = [];
  const challengeId = String(challenge?.challengeId || '').trim();
  const gameId = String(challenge?.gameId || '').trim();
  if (challengeId) {
    keys.push(`id:${challengeId}`);
  }
  if (gameId) {
    keys.push(`game:${gameId}`);
    keys.push(`id:basket-${gameId}`);
  }
  return keys;
}

export function isChallengeResolved(resolvedKeys, challenge) {
  if (!resolvedKeys?.size || !challenge) {
    return false;
  }
  return challengeIdentityKeys(challenge).some((key) => resolvedKeys.has(key));
}

export function markChallengeResolved(resolvedKeys, challenge) {
  const next = resolvedKeys instanceof Set ? resolvedKeys : new Set(resolvedKeys || []);
  challengeIdentityKeys(challenge).forEach((key) => next.add(key));
  return next;
}

function sameIncomingChallenge(left, right) {
  if (!left || !right) {
    return false;
  }
  if (left.challengeId && right.challengeId && left.challengeId === right.challengeId) {
    return true;
  }
  return Boolean(left.gameId && right.gameId && left.gameId === right.gameId);
}

function preferChallengeId(priorId, incomingId) {
  const prior = String(priorId || '');
  const incoming = String(incomingId || '');
  if (prior.startsWith('basket-') && incoming && !incoming.startsWith('basket-')) {
    return incoming;
  }
  return prior || incoming;
}

export function mergeIncomingChallenges(existing, incoming, {
  resolvedKeys = null,
  now = Date.now()
} = {}) {
  const alive = (existing || []).filter((entry) => !isChallengeExpired(entry, now));
  if (!incoming) {
    return alive;
  }
  if (isChallengeResolved(resolvedKeys, incoming) || isChallengeExpired(incoming, now)) {
    return alive.filter((entry) => !sameIncomingChallenge(entry, incoming));
  }
  const prior = alive.find((entry) => sameIncomingChallenge(entry, incoming));
  const normalized = prior
    ? {
        ...prior,
        ...incoming,
        challengeId: preferChallengeId(prior.challengeId, incoming.challengeId),
        sentAt: Math.min(
          Number(prior.sentAt) || Number.POSITIVE_INFINITY,
          Number(incoming.sentAt) || Number.POSITIVE_INFINITY
        ),
        // PeerBasket re-parses refresh expiresAt — keep the earliest deadline.
        expiresAt: Math.min(
          Number(prior.expiresAt) || Number.POSITIVE_INFINITY,
          Number(incoming.expiresAt) || Number.POSITIVE_INFINITY
        ),
        receivedAt: prior.receivedAt || incoming.receivedAt
      }
    : incoming;
  const next = alive.filter((entry) => !sameIncomingChallenge(entry, normalized));
  next.unshift(normalized);
  return next.filter((entry) => !isChallengeExpired(entry, now)).slice(0, 12);
}

export function removeIncomingChallenge(challenges, challengeIdOrChallenge) {
  if (!challengeIdOrChallenge) {
    return challenges;
  }
  if (typeof challengeIdOrChallenge === 'string') {
    return challenges.filter((entry) => entry.challengeId !== challengeIdOrChallenge);
  }
  return challenges.filter(
    (entry) => !sameIncomingChallenge(entry, challengeIdOrChallenge)
  );
}

/** True when an ACK/decline refers to our pending outgoing challenge. */
export function pendingChallengeMatches(pending, payload) {
  const challenge = pending?.challenge;
  if (!challenge || !payload) {
    return false;
  }
  if (payload.challengeId && challenge.challengeId === payload.challengeId) {
    return true;
  }
  if (payload.gameId && challenge.gameId === payload.gameId) {
    return true;
  }
  if (
    payload.challengeId
    && challenge.gameId
    && payload.challengeId === `basket-${challenge.gameId}`
  ) {
    return true;
  }
  return false;
}

export function buildChallengeDeclinePayload({
  challengeId,
  gameId = null,
  targetPlayerId,
  declinedBy
}) {
  return {
    challengeId,
    ...(gameId ? { gameId } : {}),
    targetPlayerId,
    declinedBy,
    declinedAt: Date.now()
  };
}

export function buildChallengeAckPayload({
  challengeId,
  gameId = null,
  targetPlayerId
}) {
  return {
    challengeId,
    ...(gameId ? { gameId } : {}),
    targetPlayerId,
    ackedAt: Date.now()
  };
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function openPeerIds(node) {
  return node?.getConnectionStats?.().peerIds || [];
}

function prioritizeChallengeDials(node, peerIds = []) {
  const prioritize = node?.networkAdapter?.prioritizeDial;
  if (typeof prioritize !== 'function') {
    return;
  }
  for (const peerId of peerIds) {
    prioritize.call(node.networkAdapter, peerId);
  }
}

async function deliverToOpenPeer(node, challenge, peerId, broadcastScope) {
  await node.broadcastMessage(CHALLENGE_MESSAGE_TYPE, challenge, {
    broadcastScope,
    connectToPeers: [peerId],
    fanoutPeerIds: [peerId]
  });
  return openPeerIds(node).includes(peerId);
}

/**
 * Deliver a lobby challenge over an open PeerJS link, then pause so the sender
 * does not tear down the page before the packet is flushed.
 *
 * When target peer ids are set, only those peers are connected/fanout — never
 * substitute unrelated open mesh peers. Optional out-of-band publish covers the
 * case where Online now shows the player via PeerBasket but WebRTC is still up.
 */
export async function deliverChallengeMessage(node, challenge, {
  targetPeerId = '',
  candidatePeerIds = [],
  broadcastScope,
  preferPeerIds = [],
  attempts = 6,
  settleMs = 1000,
  publishOutOfBand = null
} = {}) {
  if (!node || typeof node.broadcastMessage !== 'function') {
    throw new Error('Lobby node is not ready to deliver challenges');
  }

  const sealed = await sealChallengePayload(challenge);
  if (!sealed) {
    throw new Error('Challenge payload could not be sealed');
  }

  const targetedIds = [
    targetPeerId,
    ...candidatePeerIds
  ].filter((peerId, index, list) => peerId && list.indexOf(peerId) === index);
  const targeted = targetedIds.length > 0;
  const connectToPeers = targeted
    ? targetedIds
    : preferPeerIds.filter((peerId, index, list) => peerId && list.indexOf(peerId) === index);

  let delivered = false;
  let deliveredVia = '';
  let reachedPeerId = '';
  let outOfBandPublished = false;
  let outOfBandQueued = false;

  // Sealed payload already paid the challenge gate. Start the inbox advert
  // immediately, but do not wait on it before opening the direct link.
  const outOfBandPromise = targeted && typeof publishOutOfBand === 'function'
    ? publishOutOfBand(sealed).catch((error) => {
      lobbyDebug('challenge-deliver-oob-error', { message: error?.message || String(error) });
      return null;
    })
    : null;

  const webrtcAttempts = attempts;

  for (let attempt = 0; attempt < webrtcAttempts; attempt += 1) {
    prioritizeChallengeDials(node, connectToPeers);
    if (connectToPeers.length && typeof node.ensureConnectedToPeers === 'function') {
      await node.ensureConnectedToPeers(connectToPeers);
    }

    const open = new Set(openPeerIds(node));
    if (targeted) {
      const ordered = [
        ...targetedIds.filter((peerId) => open.has(peerId)),
        ...targetedIds.filter((peerId) => !open.has(peerId))
      ];
      for (const peerId of ordered) {
        if (!open.has(peerId)) {
          continue;
        }
        const ok = await deliverToOpenPeer(node, sealed, peerId, broadcastScope);
        if (ok) {
          delivered = true;
          deliveredVia = 'webrtc';
          reachedPeerId = peerId;
          break;
        }
      }
      if (deliveredVia === 'webrtc') {
        break;
      }
      if (attempt + 1 < webrtcAttempts) {
        await sleep(250);
      }
      continue;
    }

    const fanoutPeerIds = connectToPeers.filter((peerId) => open.has(peerId));
    await node.broadcastMessage(CHALLENGE_MESSAGE_TYPE, sealed, {
      broadcastScope,
      connectToPeers,
      ...(fanoutPeerIds.length > 0 ? { fanoutPeerIds } : {})
    });

    if (fanoutPeerIds.length > 0 || open.size > 0) {
      delivered = true;
      deliveredVia = 'webrtc';
      break;
    }
    if (attempt + 1 < webrtcAttempts) {
      await sleep(250);
    }
  }

  if (outOfBandPromise) {
    const outOfBand = await outOfBandPromise;
    outOfBandPublished = Boolean(outOfBand?.published);
    outOfBandQueued = Boolean(outOfBand?.queued || outOfBand?.advert);
    lobbyDebug('challenge-deliver-oob', {
      gameId: sealed.gameId,
      targetPlayerId: sealed.targetPlayerId,
      published: outOfBandPublished,
      queued: outOfBandQueued,
      reason: outOfBand?.reason || ''
    });
  }

  if (!delivered && outOfBandPublished) {
    delivered = true;
    deliveredVia = 'peerbasket';
  }

  await sleep(settleMs);
  const result = {
    delivered,
    deliveredVia,
    reachedPeerId,
    openPeerIds: openPeerIds(node),
    outOfBandPublished,
    outOfBandQueued,
    challenge: sealed
  };
  lobbyDebug('challenge-deliver-result', {
    gameId: sealed.gameId,
    challengeId: sealed.challengeId,
    targetPlayerId: sealed.targetPlayerId,
    delivered: result.delivered,
    deliveredVia: result.deliveredVia,
    reachedPeerId,
    openPeerIds: result.openPeerIds,
    outOfBandPublished,
    outOfBandQueued
  });
  lobbyDebugReport('CHALLENGE_SEND', {
    role: 'sender',
    challengeId: sealed.challengeId,
    gameId: sealed.gameId,
    targetPlayerId: sealed.targetPlayerId,
    challengerPeerId: sealed.challenger?.peerId,
    challengerPlayerId: sealed.challenger?.playerId,
    delivered: result.delivered,
    deliveredVia: result.deliveredVia,
    reachedPeerId,
    openPeerIds: result.openPeerIds,
    outOfBandPublished,
    outOfBandQueued,
    candidatePeerIds: targetedIds,
    hint: result.delivered
      ? 'Sender thinks challenge left this tab. On the recipient, look for CHALLENGE_RECV / challenge-ingest.'
      : 'Sender could not confirm delivery (no open WebRTC + PeerBasket publish failed/queued).'
  });
  return result;
}
